import React, { useEffect, useState, useCallback, useRef } from 'react';
import adminApi from '../api/client';

// ── Types ────────────────────────────────────────────────────────────
interface AuditLogEntry {
  id: string;
  action_type: string;
  target_type: string;
  target_id: string;
  details: string;
  ip_address: string;
  created_at: string;
  admin_name: string;
  admin_email: string;
}

interface AuditLogResponse {
  logs: AuditLogEntry[];
  total: number;
  page: number;
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

// ── Color mapping ─────────────────────────────────────────────────────
const ACTION_COLORS: Record<string, string> = {
  suspend_user:             '#D85A30',
  add_strike:               '#D85A30',
  remove_strike:            '#D85A30',
  approve_author:           '#1D9E75',
  approve_seller:           '#1D9E75',
  verify_user:              '#1D9E75',
  unsuspend_user:           '#1D9E75',
  admin_login:              '#378ADD',
  adjust_xp:                '#BA7517',
  toggle_feature_flag:      '#534AB7',
  broadcast_notification:   '#085041',
  // Author Claims lifecycle (author_claims workflow)
  approve_author_claim:            '#1D9E75',
  create_author_and_approve_claim: '#1D9E75',
  reinstate_author_claim:          '#1D9E75',
  approve_book_request:            '#1D9E75',
  request_info_author_claim:       '#534AB7',
  merge_authors:                   '#534AB7',
  reject_author_claim:             '#D85A30',
  revoke_author_claim:             '#D85A30',
  reject_book_request:             '#D85A30',
  suspend_author_claim:            '#BA7517',
  release_author_claim:            '#666666',
};

function getColor(actionType: string): string {
  return ACTION_COLORS[actionType] ?? '#9CA3AF';
}

// ── Action badge label ────────────────────────────────────────────────
const ACTION_LABELS: Record<string, string> = {
  toggle_feature_flag:    '⚑ Flag Toggle',
  broadcast_notification: '🔔 Notification',
  adjust_xp:              '⚡ XP Adjustment',
  suspend_user:           '🚫 Suspend',
  unsuspend_user:         '✅ Unsuspend',
  add_strike:             '⚠️ Strike',
  verify_user:            '✓ Verify',
  admin_login:            '🔐 Login',
  approve_author:         '✍️ Author Approve',
  approve_seller:         '🏪 Seller Approve',
  approve_author_claim:            '✍️ Claim Approved',
  create_author_and_approve_claim: '✍️ Author Created & Approved',
  request_info_author_claim:       '❓ Claim Info Requested',
  reject_author_claim:             '✗ Claim Rejected',
  suspend_author_claim:            '⏸ Claim Suspended',
  reinstate_author_claim:          '✓ Claim Reinstated',
  revoke_author_claim:             '⛔ Claim Revoked',
  approve_book_request:            '🔗 Book Request Approved',
  reject_book_request:             '✗ Book Request Rejected',
  merge_authors:                   '🔀 Authors Merged',
  release_author_claim:            '👋 Claim Released (by author)',
};

function getLabel(actionType: string): string {
  return ACTION_LABELS[actionType] ?? actionType.replace(/_/g, ' ');
}

// ── Toast Container ───────────────────────────────────────────────────
const ToastContainer: React.FC<{ toasts: Toast[]; onDismiss: (id: number) => void }> = ({ toasts, onDismiss }) => (
  <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`
          flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border
          cursor-pointer min-w-[280px]
          ${toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
          }
        `}
        onClick={() => onDismiss(toast.id)}
      >
        <span className="text-lg">{toast.type === 'success' ? '✅' : '❌'}</span>
        <span className="text-sm font-medium flex-1">{toast.message}</span>
        <button className="text-gray-400 hover:text-gray-600">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    ))}
  </div>
);

// ── Skeleton entry ────────────────────────────────────────────────────
const SkeletonEntry: React.FC = () => (
  <div className="flex gap-4 animate-pulse">
    <div className="flex flex-col items-center shrink-0 mt-1">
      <div className="w-3 h-3 rounded-full bg-gray-200" />
      <div className="w-0.5 flex-1 bg-gray-100 mt-1" />
    </div>
    <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-3 space-y-2">
      <div className="flex gap-2">
        <div className="h-5 w-24 bg-gray-200 rounded-full" />
        <div className="h-5 w-28 bg-gray-100 rounded" />
      </div>
      <div className="h-4 w-1/2 bg-gray-100 rounded" />
      <div className="h-3 w-3/4 bg-gray-100 rounded" />
    </div>
  </div>
);

// ── Main AuditLog Component ───────────────────────────────────────────
const AuditLog: React.FC = () => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toastIdRef = useRef(0);
  const LIMIT = 50;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  // ── Toast helpers ─────────────────────────────────────────────────
  const addToast = useCallback((message: string, type: 'success' | 'error') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ── Fetch logs ────────────────────────────────────────────────────
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, limit: LIMIT };
      if (filterAction) params.action_type = filterAction;
      if (filterFrom)   params.from_date = filterFrom;
      if (filterTo)     params.to_date = filterTo;

      const res = await adminApi.get<AuditLogResponse>('/admin/audit-log', { params });
      setLogs(res.data.logs ?? []);
      setTotal(res.data.total ?? 0);
    } catch {
      addToast('Failed to load audit logs.', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, filterAction, filterFrom, filterTo, addToast]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // ── Reset to page 1 when filters change ──────────────────────────
  const applyFilter = (setter: React.Dispatch<React.SetStateAction<string>>, value: string) => {
    setter(value);
    setPage(1);
  };

  const clearFilters = () => {
    setFilterAction('');
    setFilterFrom('');
    setFilterTo('');
    setPage(1);
  };

  const hasFilters = filterAction || filterFrom || filterTo;

  // ── Format timestamp ─────────────────────────────────────────────
  const formatTs = (ts: string) => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
        timeZone: 'UTC', timeZoneName: 'short',
      }).format(new Date(ts));
    } catch {
      return ts;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">📋 Audit Log</h1>
        <p className="text-sm text-gray-500 mt-1">
          Complete record of all admin actions.
          {' '}Dates and times are in UTC.
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Action type */}
        <select
          value={filterAction}
          onChange={(e) => applyFilter(setFilterAction, e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700
            focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-sm
            transition-all duration-200"
        >
          <option value="">All Actions</option>
          <option value="toggle_feature_flag">⚑ Flag Toggle</option>
          <option value="broadcast_notification">🔔 Notification</option>
          <option value="adjust_xp">⚡ XP Adjustment</option>
          <option value="suspend_user">🚫 Suspend</option>
          <option value="unsuspend_user">✅ Unsuspend</option>
          <option value="add_strike">⚠️ Strike</option>
          <option value="verify_user">✓ Verify</option>
          <option value="admin_login">🔐 Login</option>
          <option value="approve_author">✍️ Author Approve</option>
          <option value="approve_seller">🏪 Seller Approve</option>
          <option value="approve_author_claim">✍️ Claim Approved</option>
          <option value="create_author_and_approve_claim">✍️ Author Created & Approved</option>
          <option value="request_info_author_claim">❓ Claim Info Requested</option>
          <option value="reject_author_claim">✗ Claim Rejected</option>
          <option value="suspend_author_claim">⏸ Claim Suspended</option>
          <option value="reinstate_author_claim">✓ Claim Reinstated</option>
          <option value="revoke_author_claim">⛔ Claim Revoked</option>
          <option value="approve_book_request">🔗 Book Request Approved</option>
          <option value="reject_book_request">✗ Book Request Rejected</option>
          <option value="merge_authors">🔀 Authors Merged</option>
          <option value="release_author_claim">👋 Claim Released (by author)</option>
        </select>

        {/* Date from */}
        <input
          type="date"
          value={filterFrom}
          onChange={(e) => applyFilter(setFilterFrom, e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700
            focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-sm
            transition-all duration-200"
          placeholder="From"
          aria-label="From date (UTC)"
          title="From date (UTC, inclusive)"
          max={filterTo || undefined}
        />

        {/* Date to */}
        <input
          type="date"
          value={filterTo}
          onChange={(e) => applyFilter(setFilterTo, e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-700
            focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-sm
            transition-all duration-200"
          placeholder="To"
          aria-label="To date (UTC)"
          title="To date (UTC, inclusive)"
          min={filterFrom || undefined}
        />

        {/* Clear */}
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm
              text-gray-600 font-medium hover:bg-gray-50 shadow-sm transition-all duration-200"
          >
            Clear Filters
          </button>
        )}

        {/* Total count */}
        {!loading && (
          <span className="ml-auto text-sm text-gray-400">
            {total.toLocaleString()} {total === 1 ? 'entry' : 'entries'}
          </span>
        )}
      </div>

      {/* Timeline */}
      <div className="relative">
        {loading ? (
          <div className="space-y-0">
            {Array.from({ length: 8 }).map((_, i) => <SkeletonEntry key={i} />)}
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <span className="text-4xl">📭</span>
            <p className="text-gray-400 font-medium">No audit logs found.</p>
          </div>
        ) : (
          <div className="space-y-0">
            {logs.map((log, idx) => {
              const color = getColor(log.action_type);
              const isLast = idx === logs.length - 1;

              return (
                <div key={log.id} className="flex gap-4">
                  {/* Timeline stem */}
                  <div className="flex flex-col items-center shrink-0" style={{ marginTop: '1rem' }}>
                    <div
                      className="w-3 h-3 rounded-full shrink-0 ring-2 ring-white shadow"
                      style={{ backgroundColor: color }}
                    />
                    {!isLast && (
                      <div className="w-0.5 flex-1 mt-1" style={{ backgroundColor: `${color}30` }} />
                    )}
                  </div>

                  {/* Card */}
                  <div
                    className="flex-1 bg-white rounded-xl shadow-sm border border-gray-100
                      border-l-4 p-4 mb-3 hover:shadow-md transition-shadow duration-150"
                    style={{ borderLeftColor: color }}
                  >
                    {/* Row 1: badge + admin name */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="inline-flex items-center text-xs font-bold px-2.5 py-1 rounded-full"
                        style={{
                          backgroundColor: `${color}18`,
                          color,
                          border: `1px solid ${color}40`,
                        }}
                      >
                        {getLabel(log.action_type)}
                      </span>
                      <span className="text-sm font-semibold text-gray-800">
                        {log.admin_name || log.admin_email || '—'}
                      </span>
                    </div>

                    {/* Row 2: target */}
                    <p className="mt-1.5 text-xs text-gray-500">
                      <span className="font-medium text-gray-600">{log.target_type}</span>
                      {log.target_id && (
                        <>
                          {' · '}
                          <code className="font-mono text-gray-500 truncate max-w-[200px] inline-block align-bottom">
                            {log.target_id}
                          </code>
                        </>
                      )}
                    </p>

                    {/* Row 3: details */}
                    {log.details && (
                      <p className="mt-1 text-xs text-gray-400 leading-relaxed">
                        {log.details}
                      </p>
                    )}

                    {/* Row 4: ip + timestamp */}
                    <div className="mt-2 flex items-center justify-end gap-3 text-[11px] text-gray-300">
                      {log.ip_address && (
                        <span className="font-mono">{log.ip_address}</span>
                      )}
                      <span>{formatTs(log.created_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && total > LIMIT && (
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 rounded-xl text-sm font-semibold
              bg-teal-600 text-white hover:bg-teal-700
              disabled:opacity-40 disabled:cursor-not-allowed
              transition-colors duration-150 shadow-sm"
          >
            ← Previous
          </button>

          <span className="text-sm text-gray-500 font-medium">
            Page {page} of {totalPages}
          </span>

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 rounded-xl text-sm font-semibold
              bg-teal-600 text-white hover:bg-teal-700
              disabled:opacity-40 disabled:cursor-not-allowed
              transition-colors duration-150 shadow-sm"
          >
            Next →
          </button>
        </div>
      )}

      {/* Toasts */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default AuditLog;
